import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { cellText, sortMostLeftToDoFirst, splitScoredStudents, windowMode, type WindowMode } from '../benchmarkMonitoring';
import { BENCH } from '../tokens';
import type { BenchmarkMonitoring, MonitoringCell, MonitoringStudent } from '../types';
import { STATUS_COLORS, StatusDot } from './ProgressBar';
import { CARD_SX, CardHeading, HeaderCell, NAME_WIDTH, NameCell, ROW_SX } from './tableParts';

const GRADE_WIDTH = 56;
const FIXED_WIDTH = 160;

function dotColor(cell: MonitoringCell): string | null {
  switch (cell.state) {
    case 'scored':
      return STATUS_COLORS[0];
    case 'toScore':
      return STATUS_COLORS[1];
    case 'inProgress':
      return STATUS_COLORS[2];
    case 'notStarted':
      return STATUS_COLORS[3];
    default:
      return null;
  }
}

function StatusCell({ cell, mode, now, wide }: { cell: MonitoringCell; mode: WindowMode; now: Date; wide?: boolean }) {
  const color = dotColor(cell);
  const done = cell.state === 'scored' || cell.state === 'off';
  return (
    <Box
      data-testid={`cell-${cell.key}`}
      sx={{ flex: wide ? 1 : `0 0 ${FIXED_WIDTH}px`, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      {color && <StatusDot color={color} />}
      <Typography component="span" sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: done ? BENCH.muted : BENCH.ink, whiteSpace: 'nowrap' }}>
        {cellText(cell, mode, now)}
      </Typography>
    </Box>
  );
}

function StudentRow({ student, mode, now }: { student: MonitoringStudent; mode: WindowMode; now: Date }) {
  const cell = (key: MonitoringCell['key']): MonitoringCell => student.cells.find((c) => c.key === key) ?? { key, state: 'notStarted' };
  return (
    <Box data-testid="student-row" sx={{ ...ROW_SX, py: '12px' }}>
      <NameCell name={student.name} />
      <Typography component="span" sx={{ flex: `0 0 ${GRADE_WIDTH}px`, fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', color: BENCH.ink }}>
        {student.grade}
      </Typography>
      <StatusCell cell={cell('speaking')} mode={mode} now={now} wide />
      <StatusCell cell={cell('writing')} mode={mode} now={now} wide />
    </Box>
  );
}

export function StudentsTable({ data, students, now }: { data: BenchmarkMonitoring; students: MonitoringStudent[]; now: Date }) {
  const [showDone, setShowDone] = useState(false);
  const mode = windowMode(data);
  const { todo, done } = splitScoredStudents(sortMostLeftToDoFirst(students, now));
  return (
    <Box component="section" data-testid="students-table" sx={CARD_SX}>
      <CardHeading title="Students" right="Most left to do first" />
      <Box data-testid="status-header" sx={{ display: 'flex', alignItems: 'center', gap: '16px', pb: '8px', borderBottom: `2px solid ${BENCH.headerRule}` }}>
        <HeaderCell width={NAME_WIDTH}>Student</HeaderCell>
        <HeaderCell width={GRADE_WIDTH}>Grade</HeaderCell>
        <HeaderCell>Speaking</HeaderCell>
        <HeaderCell>Writing</HeaderCell>
      </Box>
      {students.length === 0 && (
        <Typography component="p" sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', color: BENCH.muted, py: '16px', m: 0 }}>
          No students rostered.
        </Typography>
      )}
      {todo.map((student) => (
        <StudentRow key={student.id} student={student} mode={mode} now={now} />
      ))}
      {showDone && done.map((student) => <StudentRow key={student.id} student={student} mode={mode} now={now} />)}
      {done.length > 0 && (
        <Box
          data-testid="scored-students"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '16px', borderTop: `1px solid ${BENCH.divider}` }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StatusDot color={STATUS_COLORS[0]} />
            <Typography component="span" sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: BENCH.ink }}>
              {`${done.length} ${done.length === 1 ? 'student' : 'students'} with every benchmark scored`}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            aria-expanded={showDone}
            onClick={() => setShowDone((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontFamily: BENCH.font,
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: 700,
              color: BENCH.action,
              background: 'none',
              border: 0,
              p: 0,
              cursor: 'pointer',
            }}
          >
            {showDone ? 'Hide' : 'Show'}
            {showDone ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
          </Box>
        </Box>
      )}
    </Box>
  );
}
